import * as THREE from 'three';
import WallController from '../controllers/wall-controller.js';
import DoorController from '../controllers/door-controller.js';
import WindowController from '../controllers/window-controller.js';
import FurnitureController from '../controllers/furniture-controller.js';

class SceneManager {
  constructor() {
    this.scene = new THREE.Scene();
    this.lights = [];
    this.grid = null;
    this.wallController = null;
    this.doorController = null;
    this.windowController = null;
    this.furnitureController = null;
  }

  init() {
    this.setupBackground();
    this.setupLighting();
    this.setupGrid();

    this.wallController      = new WallController(this);
    this.doorController      = new DoorController(this, this.wallController);
    this.windowController    = new WindowController(this, this.wallController);
    this.furnitureController = new FurnitureController(this, this.wallController);

    console.log('✔ Scene initialized');
  }

  setupBackground() {
    // Il background viene sovrascritto da engine.setupEnvironment(),
    // ma lo imposto qui come valore di fallback iniziale
    this.scene.background = new THREE.Color(0x1a1a2e);
    this.scene.fog = new THREE.Fog(0x1a1a2e, 30, 60);
  }

  setupLighting() {
    /**
     * Due sorgenti di luce leggere + env map IBL (gestita da engine.setupEnvironment):
     *
     *  1. AmbientLight (molto tenue)
     *     → garantisce visibilità minima a grid, wireframe e materiali MeshBasic
     *        che non beneficiano dell'env map
     *
     *  2. DirectionalLight principale
     *     → definisce la direzione della luce solare per ombre e highlights speculari
     *        sui materiali PBR; complementa l'IBL senza duplicarla
     *
     *  IBL via scene.environment (RoomEnvironment, configurato in engine.js):
     *     → illuminazione ambientale e di rimbalzo per MeshStandardMaterial /
     *        MeshPhysicalMaterial (tutti i modelli GLTF e le porte/finestre)
     *     → costo GPU fisso, indipendente dal numero di oggetti in scena
     *     → gestisce automaticamente riflessi, roughness, vetri (transmission)
     */

    // Luce ambientale tenue per elementi non-PBR (grid, preview wireframe)
    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambient);
    this.lights.push(ambient);

    // Luce solare direzionale
    const sunLight = new THREE.DirectionalLight(0xfff5e0, 1.8);
    sunLight.position.set(8, 20, 12);
    this.scene.add(sunLight);
    this.lights.push(sunLight);
  }

  setupGrid() {
    this.grid = new THREE.GridHelper(20, 20, 0x555555, 0x333333);
    this.grid.material.opacity = 0.5;
    this.grid.material.transparent = true;
    this.scene.add(this.grid);

    // Piano collider invisibile per i raycaster ground-plane
    const planeGeometry = new THREE.PlaneGeometry(50, 50);
    const planeMaterial = new THREE.MeshBasicMaterial({
      visible: false,
      side: THREE.DoubleSide
    });
    const plane = new THREE.Mesh(planeGeometry, planeMaterial);
    plane.rotation.x = -Math.PI / 2;
    this.scene.add(plane);
  }

  update(delta) {
    // Update loop per animazioni future
  }

  getScene()               { return this.scene; }
  getWallController()      { return this.wallController; }
  getDoorController()      { return this.doorController; }
  getWindowController()    { return this.windowController; }
  getFurnitureController() { return this.furnitureController; }

  dispose() {
    this.lights.forEach(light => {
      this.scene.remove(light);
      if (light.dispose) light.dispose();
    });

    if (this.grid) {
      this.scene.remove(this.grid);
      this.grid.geometry.dispose();
      this.grid.material.dispose();
    }

    // Libera l'env map se presente
    if (this.scene.environment) {
      this.scene.environment.dispose();
      this.scene.environment = null;
    }

    if (this.wallController)      this.wallController.dispose();
    if (this.doorController)      this.doorController.dispose();
    if (this.windowController)    this.windowController.dispose();
    if (this.furnitureController) this.furnitureController.dispose();
  }
}

export default SceneManager;